import './PersonalSubscription.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  Form,
  FormItem,
  Header,
  Input,
  List,
  ListItem,
  Placeholder,
  ScrollView,
  Select,
  Switch,
} from '$uikit';
import { AmountInput, Loader, SubscriptionIcon } from '$shared/components';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { Api } from '$api';
import { formatInputAmount } from '$utils/number';
import { toastActions } from '$store/toast';
import { mainActions, mainSelector } from '$store/main';
import { CurrencyEnum } from '$store/models';
import {
  _confirm,
  formatCurrency,
  roundToDecimal,
  subscriptionDiscountPrice,
} from '$utils';

const MAX_DESCRIPTION_LEN = 250;

export const PersonalSubscription: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const [loadingState, setLoadingState] = useState('loading');
  const [data, setData] = useState<{
    isEnabled: boolean;
    price: string;
    tonPrice: number;
    message: string;
    discount: number;
  } | null>(null);
  const user = useCurrentUserInfo();
  const { feePercent } = useSelector(mainSelector);
  const currency = user.verified > 0 ? CurrencyEnum.EUR : CurrencyEnum.TON;

  const loadData = useCallback(() => {
    setLoadingState('loading');
    Api.get('/balance/subscription/personal')
      .then((resp) => {
        const data: any = resp;
        setData({
          ...data,
          price: data.price || '',
          message: data.message || '',
          discount: data.discount || 0,
        });
        setLoadingState('none');
      })
      .catch(() => {
        setLoadingState('failed');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const handleEnable = useCallback(() => {
    setData((old) => ({ ...old!, isEnabled: true }));
  }, []);

  const handleDisable = () => {
    _confirm(t('personal_subscription_disable_confirmation'), () => {
      dispatch(toastActions.setToastLoading());
      Api.post('/balance/subscription/personal/edit', {
        price: 0,
      })
        .then(() => {
          dispatch(toastActions.hideToast());
          dispatch(mainActions.setHasPersonalSubscription(false));
          setData((old) => ({ ...old!, price: '', isEnabled: false }));
          window.history.back();
        })
        .catch((e) => {
          dispatch(toastActions.setToastFail(e.message));
        });
    });
  };

  const handleSave = useCallback(() => {
    if (+data!.price < 1) {
      dispatch(
        toastActions.setToastFail(
          t('personal_subscription_min_price', { price: formatCurrency(1, currency) }),
        ),
      );
      return;
    }
    dispatch(toastActions.setToastLoading());
    Api.post('/balance/subscription/personal/edit', {
      price: +data!.price,
      message: data!.message,
      discount: data?.discount ? data?.discount : 0,
    })
      .then(() => {
        dispatch(toastActions.setToastSuccess());
        dispatch(mainActions.setHasPersonalSubscription(!!data!.price));
        window.history.back();
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
      });
  }, [dispatch, data, t, currency]);

  const priceTip = useMemo(() => {
    if (!data) {
      return null;
    }

    if (+data.price < 5) {
      return t('personal_subscription_min_price', { price: formatCurrency(5, currency) });
    } else if (+data.price > 50) {
      return t('personal_subscription_max_price', {
        price: formatCurrency(50, currency),
      });
    } else {
      if (currency === CurrencyEnum.EUR) {
        return t('personal_subscription_price_tip_eur', {
          price: formatCurrency(+data.price * (1 - feePercent), currency),
        });
      }

      return t('personal_subscription_price_tip', {
        price: formatCurrency(+data.price * (1 - feePercent), currency),
        usdPrice: Number(+data.price * data.tonPrice).toPrecision(2),
      });
    }
  }, [data, t, currency]);

  const handleChangeFormItem = (key: string, value: string | number) => {
    setData((old) => ({ ...old!, [key]: value }));
  };

  const handleChangePrice = ({ target }) => {
    const value = formatInputAmount(target.value);
    handleChangeFormItem('price', formatInputAmount(target.value));
    if (parseInt(value) < 10 && data?.discount === 90) {
      handleChangeFormItem('discount', 80);
    }
  };

  const handleChangeDescription = ({ target }) =>
    handleChangeFormItem('message', target.value.slice(0, MAX_DESCRIPTION_LEN));

  const handleDiscountClick = () => {
    handleChangeFormItem('discount', data?.discount ? 0 : 50);
  };

  function renderContent() {
    if (loadingState === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (loadingState === 'loading' || !data) {
      return <Loader isWrapped />;
    }

    const discountPrice = subscriptionDiscountPrice(
      parseFloat(data.price),
      data?.discount || 100,
    );
    const profit = roundToDecimal(discountPrice - discountPrice * feePercent, 2);

    return (
      <>
        <div className="PersonalSubscription__info">
          <SubscriptionIcon type={3} authorId={0} />
          <div className="PersonalSubscription__info__title">
            {t('personal_subscription_info')}
          </div>
        </div>
        <Form>
          <FormItem title={t('personal_subscription_price_title')} description={priceTip}>
            <Input
              type="price"
              currency={currency}
              placeholder={t('personal_subscription_price_placeholder')}
              onChange={handleChangePrice}
              value={`${data.price}`}
            />
          </FormItem>
          <FormItem>
            <List>
              <ListItem
                indicator={
                  <Switch checked={!!data?.discount} onClick={handleDiscountClick} />
                }
              >
                {t('personal_subscription_discount_title')}
              </ListItem>
            </List>
          </FormItem>
          {!!data?.discount && (
            <FormItem
              description={t('personal_subscription_discount_percent_description', {
                price: formatCurrency(discountPrice, CurrencyEnum.EUR),
                profit: formatCurrency(profit, CurrencyEnum.EUR),
              })}
            >
              <Select
                value={data?.discount || 0}
                onChange={(e) => {
                  handleChangeFormItem('discount', parseFloat(e.target.value));
                }}
              >
                {Array.from(
                  { length: parseFloat(data.price) >= 10 ? 9 : 8 },
                  (_, i) => i + 1,
                ).map((percent) => {
                  const value = percent * 10;
                  return (
                    <option key={value} value={value}>
                      {value}%
                    </option>
                  );
                })}
              </Select>
            </FormItem>
          )}
          <FormItem
            title={t('personal_subscription_description_title')}
            description={
              <>
                <div>{t('personal_subscription_description_tip')}</div>
                <div>
                  {t('symbols_limit', {
                    count: MAX_DESCRIPTION_LEN - data.message.length,
                  })}
                </div>
              </>
            }
          >
            <Input
              placeholder={t('personal_subscription_description_placeholder')}
              onChange={handleChangeDescription}
              value={`${data.message}`}
              multiline
            />
          </FormItem>
          <br />
          <FormItem>
            <Button onClick={handleSave}>{t('save')}</Button>
          </FormItem>
          <FormItem>
            <Button mode="secondary" onClick={handleDisable}>
              {t('personal_subscription_disable')}
            </Button>
          </FormItem>
        </Form>
      </>
    );
  }

  return (
    <>
      <Header showBackButton>{t('personal_subscription_title')}</Header>
      <ScrollView>{renderContent()}</ScrollView>
    </>
  );
};
